import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_DATA = {
  brass:       { label: "Brass Play Button",       gradient: "from-amber-700 via-yellow-600 to-amber-800",    glow: "rgba(181,101,29,0.5)" },
  copper:      { label: "Copper Play Button",      gradient: "from-orange-500 via-amber-600 to-orange-700",   glow: "rgba(184,115,51,0.55)" },
  silver:      { label: "Silver Play Button",      gradient: "from-slate-300 via-slate-100 to-slate-400",     glow: "rgba(192,192,192,0.5)" },
  gold:        { label: "Gold Play Button",        gradient: "from-yellow-300 via-amber-200 to-yellow-500",   glow: "rgba(255,215,0,0.6)" },
  diamond:     { label: "Diamond Play Button",     gradient: "from-cyan-200 via-white to-blue-200",           glow: "rgba(185,242,255,0.7)" },
  red_diamond: { label: "Red Diamond Play Button", gradient: "from-red-400 via-rose-500 to-red-700",          glow: "rgba(211,47,47,0.7)" },
};

/**
 * Polls for unseen follower-milestone celebrations and shows a one-time
 * congratulatory modal. Runs in the background; pops the queue one at a time.
 */
export default function CelebrationListener() {
  const { user, getAuthHeaders } = useAuth();
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchCelebs = async () => {
      try {
        const r = await axios.get(`${API}/users/celebrations/unseen`, { headers: getAuthHeaders() });
        if (!cancelled && r.data?.length) setQueue(q => [...q, ...r.data]);
      } catch (e) { /* silent */ }
    };
    fetchCelebs();
    const id = setInterval(fetchCelebs, 60_000);  // every minute
    return () => { cancelled = true; clearInterval(id); };
  }, [user, getAuthHeaders]);

  useEffect(() => {
    if (!active && queue.length > 0) {
      setActive(queue[0]);
      setQueue(q => q.slice(1));
    }
  }, [queue, active]);

  if (!active) return null;
  const isPlayButton = !!active.play_button;
  const badge = isPlayButton ? BADGE_DATA[active.play_button] : null;

  return (
    <Dialog open={!!active} onOpenChange={() => setActive(null)}>
      <DialogContent className="bg-[#0A0A0F] border-violet-500/40 text-white max-w-md overflow-hidden" data-testid="milestone-modal">
        <div className="absolute inset-0 -z-10 opacity-30" style={{ background: `radial-gradient(circle at top, ${badge?.glow || "rgba(139,92,246,0.4)"}, transparent 60%)` }} />
        <button onClick={() => setActive(null)} className="absolute top-3 right-3 text-gray-500 hover:text-white" data-testid="milestone-close"><X className="w-4 h-4" /></button>

        <div className="text-center py-6">
          <Sparkles className="w-7 h-7 text-violet-400 mx-auto animate-pulse" />
          <p className="text-[11px] uppercase tracking-[0.3em] text-violet-300 mt-3 font-semibold">Congratulations!</p>

          <h2 className="text-3xl font-bold text-white mt-2" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="milestone-count">
            {active.milestone.toLocaleString()} Followers
          </h2>
          <p className="text-sm text-gray-400 mt-1">You just hit a major creator milestone on AIxTribe 🎉</p>

          {isPlayButton && (
            <div className="mt-6 mx-auto w-fit">
              <div className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${badge.gradient} flex items-center justify-center shadow-2xl mx-auto`} style={{ boxShadow: `0 0 60px ${badge.glow}` }}>
                <svg viewBox="0 0 24 24" className="w-14 h-14 text-black/80 drop-shadow"><polygon points="6,4 6,20 20,12" fill="currentColor" /></svg>
              </div>
              <p className="mt-3 text-sm font-semibold text-white">{badge.label}</p>
              <p className="text-xs text-gray-500">now visible on your profile</p>
            </div>
          )}

          {active.milestone === 100000 && (
            <div className="mt-4 mx-4 p-3 rounded-xl bg-violet-500/10 border border-violet-500/20 text-left">
              <p className="text-[11px] uppercase tracking-wider text-violet-300 font-semibold">Monetization Eligibility</p>
              <p className="text-xs text-gray-300 mt-1">✅ <strong>1 of 2 criteria</strong> unlocked. Hit 4,000 watch hours on long-form videos to start earning ₹50/1000 views.</p>
            </div>
          )}

          <Button onClick={() => setActive(null)} className="mt-6 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-8" data-testid="milestone-cta">
            Awesome — keep going!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
